/* eslint-disable react/no-unescaped-entities */
import { Home, Users, Building2, AlertTriangle, CheckCircle2, Euro, Calendar } from 'lucide-react';
import { Link } from 'react-router-dom';
import GermanyResourceShell from './_components/GermanyResourceShell';

const DESC = 'Germany student accommodation 2026 — Studentenwerk dorms vs WG shared flats vs private rentals. Costs by city, booking timeline & how to avoid scams.';

const OPTIONS = [
  { type: 'Studentenwerk Dormitory',  cost: '€220 – €400/mo',  pros: 'Cheapest · pre-furnished · admin included',         cons: '12–18 mo waiting list · limited slots',         best: 'First-year students who book 12+ months early' },
  { type: 'WG (Shared Flat)',          cost: '€350 – €600/mo',  pros: 'Social community · flexible · negotiable',          cons: 'Mitbewohner interview required · subletting issues', best: 'Students with German B1+ · easy entry to social life' },
  { type: 'Private Rental (Studio)',   cost: '€600 – €1,100/mo', pros: 'Full privacy · long-term security · pet-friendly',  cons: 'Highest deposit (3 mo rent) · agency fees',     best: 'Couples / PhD students / families' },
  { type: 'Boarding-Style Apartments', cost: '€650 – €1,200/mo', pros: 'Move-in ready · short-term contracts (3+ mo) · utility-included', cons: 'Highest €/sqm rate · short-term lock-in',  best: 'First 2–3 months while you find permanent housing' },
];

const CITIES = [
  { city: 'Munich',     dorm: '€350–€450', wg: '€600–€800', studio: '€900–€1,300', note: 'Highest demand · book 18 mo early' },
  { city: 'Frankfurt',  dorm: '€300–€420', wg: '€500–€700', studio: '€800–€1,100', note: 'Financial-district premium' },
  { city: 'Berlin',     dorm: '€280–€380', wg: '€450–€650', studio: '€700–€1,000', note: 'Most flexibility but tight market' },
  { city: 'Hamburg',    dorm: '€280–€380', wg: '€450–€650', studio: '€700–€950',   note: 'Coastal premium · good mix' },
  { city: 'Stuttgart',  dorm: '€260–€360', wg: '€400–€600', studio: '€650–€900',   note: 'Auto-industry hub · cheaper outskirts' },
  { city: 'Leipzig',    dorm: '€200–€300', wg: '€280–€450', studio: '€450–€650',   note: 'Cheapest among university cities' },
  { city: 'Dresden',    dorm: '€220–€320', wg: '€300–€480', studio: '€500–€700',   note: 'Excellent value · low waiting time' },
];

const TIMELINE = [
  { stage: '12 months before',  action: 'Register on Studentenwerk waiting list in your target city. Free, no commitment.' },
  { stage: '8 months before',   action: 'Start screening WG-Gesucht.de and ImmoScout24 listings. Save target listings.' },
  { stage: '4 months before',   action: 'Begin emailing WG ads. Expect 1 reply per 10–15 messages. Include CV + photo + Selbstbeschreibung in German.' },
  { stage: '2 months before',   action: 'Confirm Studentenwerk allocation OR book a 3-month boarding apartment as a buffer. Send signed contract to embassy.' },
  { stage: '2 weeks before',    action: 'Travel insurance + first-month rent transfer + Anmeldung appointment booking (mandatory within 14 days of arrival).' },
  { stage: 'Week 1 in Germany', action: 'Complete Anmeldung at Bürgeramt → open Girokonto → release blocked account → activate health insurance.' },
];

const SCAMS = [
  'Never pay rent before signing a written contract — German law requires written rental agreements.',
  'Beware "landlords" who refuse video calls or in-person viewings via Skype/WhatsApp from "out-of-country".',
  'Never wire money to a private account before you have signed Mietvertrag (rental contract) in your hand.',
  'Verify Studentenwerk emails come from official .de domains — phishing emails impersonate every German university.',
  'Reject any "agent" charging more than 1 month rent as commission — German law caps broker fees at 2 months max.',
];

const FAQS = [
  { q: 'What is the cheapest accommodation option for Indian students in Germany?',
    a: 'Studentenwerk (state-run student dormitories) are the cheapest at €220–€400/month including utilities and internet. However, demand is high and waiting lists run 12–18 months. The realistic cheapest path: get on the Studentenwerk waiting list immediately AND simultaneously hunt for a WG (shared flat) at €350–€500/month outside Munich/Frankfurt. East-German cities (Leipzig, Dresden) reduce these costs by 30–40%.' },
  { q: 'When should I start looking for accommodation in Germany?',
    a: '12 months before arrival is ideal. Studentenwerk waiting lists open as soon as you have a university admission letter — register immediately. WG hunting starts 4 months before arrival via WG-Gesucht.de and ImmoScout24. The single biggest mistake Indian students make is starting their search 1–2 months before travel — at that point you will only find expensive boarding apartments (€800+/month).' },
  { q: 'What is a WG and how does it work in Germany?',
    a: 'WG (Wohngemeinschaft) is a German shared-flat culture — 2–6 students sharing a 3–7 bedroom apartment with private bedrooms + shared kitchen, bathroom, and living areas. Each tenant has their own contract OR there is one Hauptmieter (main tenant) who sublets. Rents range €350–€700/month per room. The "WG-casting" (interview) by existing flatmates is the unique German feature — they pick who joins. B1 German massively improves your odds.' },
  { q: 'How much deposit do I need for German student accommodation?',
    a: 'Studentenwerk: typically 1 month rent (~€300). WG: 2–3 months rent (€700–€1,800). Private rental: 3 months rent capped by law (Kautionsobergrenze). The deposit is refundable when you move out PROVIDED the apartment is in original condition. Pre-existing damages must be photographed and noted in the Übergabeprotokoll (handover protocol) on Day 1 of move-in.' },
  { q: 'Can I rent an apartment from India before arriving in Germany?',
    a: 'Yes — Studentenwerk allocations, university-affiliated WG schemes, and boarding-apartment chains (StudentApart, Speicher Bonn) accept signed contracts from abroad. However, regular private landlords ALMOST ALWAYS refuse to rent without an in-person viewing + SCHUFA (German credit score) check. Strategy: book a Studentenwerk slot OR a 3-month boarding apartment from India, then hunt for permanent housing in your first month on the ground.' },
  { q: 'Is health insurance included in Studentenwerk rent?',
    a: 'No — health insurance is a separate mandatory cost (€125–€130/month for under-30 students via TK, AOK, or Barmer). Studentenwerk rent typically includes utilities (electricity, heating, water), basic internet, and shared cleaning. Some Studentenwerks bill a small additional "Semesterbeitrag" (€40–€80/sem) for facilities access.' },
  { q: 'What is a Schufa check and do international students need one?',
    a: 'Schufa is Germany\'s central credit-scoring agency — every long-term rental application requires a Schufa-Auskunft (credit report). International students freshly arrived have no Schufa history, which makes private landlords nervous. Two workarounds: (1) Studentenwerk and university WGs don\'t require Schufa; (2) for private rentals, offer 3-months rent upfront as a "Sicherheitsgarantie" — almost all landlords accept this as a Schufa substitute. Begin building your Schufa from Month 1 by signing a German mobile contract.' },
  { q: 'How much notice do I need to give before moving out of a German rental?',
    a: 'Standard tenant notice is 3 months under German law (§573c BGB) — applies to all WG main contracts and private rentals. Studentenwerk dorms typically have shorter notice (4–8 weeks, depending on the city). Always submit notice in writing (signed letter, not WhatsApp/email) within the contractual window. Improper notice = you owe rent until the next legal exit date. Subletting your room to cover the notice period is allowed only with written landlord permission.' },
];

const GermanyStudentAccommodationPage = () => (
  <GermanyResourceShell
    slug="student-accommodation"
    breadcrumbLabel="Student Accommodation"
    articleHeadline="Germany Student Accommodation 2026 — Complete Housing Guide"
    articleDescription={DESC}
    faqs={FAQS}
    related={['cost-of-study', 'student-visa', 'intakes']}
  >
    {/* Hero */}
    <section className="relative overflow-hidden" style={{ background: 'linear-gradient(135deg, #003087 0%, #001F5C 60%, #020617 100%)' }}>
      <div className="relative max-w-7xl mx-auto px-6 py-16 lg:py-20">
        <div className="max-w-3xl">
          <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm border border-white/20 rounded-full px-4 py-1.5 mb-5">
            <Home className="w-3.5 h-3.5 text-[#C5A028]" />
            <span className="text-xs text-white/90 font-medium">Germany Resource — Student Accommodation</span>
          </div>
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold font-syne text-white leading-tight mb-4">
            Germany Student Accommodation 2026 — Dorms, WGs &amp; Private Flats
          </h1>
          <p className="text-base lg:text-lg text-slate-300 leading-relaxed mb-6">
            Housing is the single biggest expense for Indian students in Germany — and the biggest source of first-month stress. This guide compares Studentenwerk dorms, WG shared flats, private studios, and boarding apartments — with cost-by-city tables, the 12-month booking timeline that actually works, and the 5 most common housing scams to avoid.
          </p>
          <div className="grid grid-cols-3 gap-3 max-w-xl">
            <div className="bg-white/5 border border-white/10 rounded-xl p-3">
              <div className="text-[10px] text-slate-400 uppercase tracking-wider mb-0.5">Cheapest</div>
              <div className="text-base font-bold text-[#C5A028]">€220/mo</div>
            </div>
            <div className="bg-white/5 border border-white/10 rounded-xl p-3">
              <div className="text-[10px] text-slate-400 uppercase tracking-wider mb-0.5">Start Hunting</div>
              <div className="text-base font-bold text-[#C5A028]">12 mo ahead</div>
            </div>
            <div className="bg-white/5 border border-white/10 rounded-xl p-3">
              <div className="text-[10px] text-slate-400 uppercase tracking-wider mb-0.5">Deposit Cap</div>
              <div className="text-base font-bold text-[#C5A028]">3 mo rent</div>
            </div>
          </div>
        </div>
      </div>
    </section>

    {/* 4 Options Comparison */}
    <section className="py-14 lg:py-18 bg-white">
      <div className="max-w-6xl mx-auto px-6">
        <h2 className="text-2xl lg:text-3xl font-bold font-syne text-[#003087] mb-2">4 Accommodation Options Compared</h2>
        <p className="text-sm text-slate-600 mb-6">Costs are nationwide averages. Munich/Frankfurt add 30%; East-German cities reduce 30%.</p>
        <div className="space-y-3">
          {OPTIONS.map((o) => (
            <div key={o.type} className="bg-slate-50 border border-slate-200 rounded-xl p-5">
              <div className="flex flex-wrap items-start justify-between gap-3 mb-3">
                <h3 className="text-lg font-bold font-syne text-[#003087]">{o.type}</h3>
                <div className="text-right">
                  <div className="text-[10px] text-slate-500 uppercase tracking-wider">Monthly Rent</div>
                  <div className="text-base font-bold text-emerald-700">{o.cost}</div>
                </div>
              </div>
              <div className="grid md:grid-cols-3 gap-3 text-sm mb-3">
                <div>
                  <div className="text-[10px] font-bold text-emerald-700 uppercase tracking-wider mb-0.5">Pros</div>
                  <div className="text-slate-700">{o.pros}</div>
                </div>
                <div>
                  <div className="text-[10px] font-bold text-amber-700 uppercase tracking-wider mb-0.5">Cons</div>
                  <div className="text-slate-700">{o.cons}</div>
                </div>
                <div>
                  <div className="text-[10px] font-bold text-[#003087] uppercase tracking-wider mb-0.5">Best For</div>
                  <div className="text-slate-700 font-medium">{o.best}</div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>

    {/* Cost by city */}
    <section className="py-14 lg:py-18 bg-slate-50">
      <div className="max-w-6xl mx-auto px-6">
        <h2 className="text-2xl lg:text-3xl font-bold font-syne text-[#003087] mb-2">Accommodation Costs by City (2026)</h2>
        <p className="text-sm text-slate-600 mb-6">Monthly rent in EUR — Studentenwerk dorm · WG room · private studio.</p>
        <div className="overflow-x-auto bg-white border border-slate-200 rounded-xl">
          <table className="w-full text-sm">
            <thead className="bg-[#003087] text-white">
              <tr>
                <th className="px-4 py-3 text-left font-syne">City</th>
                <th className="px-4 py-3 text-left font-syne">Studentenwerk</th>
                <th className="px-4 py-3 text-left font-syne">WG Room</th>
                <th className="px-4 py-3 text-left font-syne">Private Studio</th>
                <th className="px-4 py-3 text-left font-syne">Note</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-200 bg-white">
              {CITIES.map((c) => (
                <tr key={c.city}>
                  <td className="px-4 py-3 font-bold text-[#003087]">{c.city}</td>
                  <td className="px-4 py-3">{c.dorm}</td>
                  <td className="px-4 py-3">{c.wg}</td>
                  <td className="px-4 py-3 font-semibold">{c.studio}</td>
                  <td className="px-4 py-3 text-slate-600 text-xs">{c.note}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </section>

    {/* Booking timeline */}
    <section className="py-14 lg:py-18 bg-white">
      <div className="max-w-5xl mx-auto px-6">
        <h2 className="text-2xl lg:text-3xl font-bold font-syne text-[#003087] mb-7">12-Month Booking Timeline</h2>
        <div className="space-y-3">
          {TIMELINE.map((t, i) => (
            <div key={i} className="bg-slate-50 border border-slate-200 rounded-xl p-5 flex gap-4">
              <Calendar className="w-6 h-6 text-[#003087] shrink-0 mt-0.5" />
              <div className="flex-1">
                <div className="flex flex-wrap items-baseline gap-2 mb-1.5">
                  <h3 className="text-base font-bold font-syne text-[#003087]">{t.stage}</h3>
                </div>
                <p className="text-sm text-slate-700 leading-relaxed">{t.action}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>

    {/* Scams */}
    <section className="py-14 lg:py-18 bg-amber-50/40">
      <div className="max-w-4xl mx-auto px-6">
        <h2 className="text-2xl lg:text-3xl font-bold font-syne text-[#003087] mb-5">5 Common Housing Scams to Avoid</h2>
        <div className="space-y-3">
          {SCAMS.map((s, i) => (
            <div key={i} className="bg-white border border-amber-200 rounded-xl p-4 flex gap-3">
              <AlertTriangle className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
              <p className="text-[15px] text-slate-700 leading-7">{s}</p>
            </div>
          ))}
        </div>
      </div>
    </section>

    {/* Anmeldung Section */}
    <section className="py-14 lg:py-18 bg-white">
      <div className="max-w-5xl mx-auto px-6">
        <h2 className="text-2xl lg:text-3xl font-bold font-syne text-[#003087] mb-3">Anmeldung — The Single Most Important Post-Arrival Step</h2>
        <p className="text-[15px] leading-7 text-slate-700 mb-6">
          <em>Anmeldung</em> (address registration) is the legal step that converts you from "tourist with a visa" to "resident". Under §17 of the Bundesmeldegesetz, you MUST register your German address at the local <em>Bürgeramt</em> within 14 days of move-in. Miss this deadline and you cannot do ANY of the following: open a Girokonto, release blocked-account funds, activate health insurance, get a Semesterticket, or even buy a mobile SIM.
        </p>

        <h3 className="text-lg font-bold font-syne text-[#003087] mb-3">Anmeldung Document Checklist</h3>
        <div className="bg-slate-50 border border-slate-200 rounded-xl p-5 mb-6">
          <ul className="space-y-2 text-sm text-slate-700">
            <li className="flex items-start gap-2"><CheckCircle2 className="w-5 h-5 text-emerald-600 shrink-0 mt-0.5" /><span><strong>Wohnungsgeberbestätigung</strong> — your landlord's "confirmation of move-in" (required by law since 2015). They must provide this within 14 days.</span></li>
            <li className="flex items-start gap-2"><CheckCircle2 className="w-5 h-5 text-emerald-600 shrink-0 mt-0.5" /><span><strong>Mietvertrag</strong> (Rental contract) — original copy</span></li>
            <li className="flex items-start gap-2"><CheckCircle2 className="w-5 h-5 text-emerald-600 shrink-0 mt-0.5" /><span><strong>Valid passport + visa</strong> — student visa stamp must be readable</span></li>
            <li className="flex items-start gap-2"><CheckCircle2 className="w-5 h-5 text-emerald-600 shrink-0 mt-0.5" /><span><strong>Anmeldeformular</strong> — downloadable from your city's Bürgeramt website, pre-fill before appointment</span></li>
          </ul>
        </div>

        <div className="grid md:grid-cols-2 gap-4">
          <div className="bg-amber-50/50 border border-amber-200 rounded-xl p-5">
            <h3 className="text-base font-bold font-syne text-amber-800 mb-2">⚠ The Anmeldung Appointment Bottleneck</h3>
            <p className="text-sm text-slate-700 leading-relaxed">
              In Munich, Berlin, Frankfurt, and Hamburg, Bürgeramt appointments are booked out 8–12 weeks in advance. Book your appointment slot BEFORE you fly to Germany (24/7 online portal — check your city's official .de site).
            </p>
          </div>
          <div className="bg-emerald-50/50 border border-emerald-200 rounded-xl p-5">
            <h3 className="text-base font-bold font-syne text-emerald-800 mb-2">✓ The Walk-In Backup</h3>
            <p className="text-sm text-slate-700 leading-relaxed">
              Most Bürgeramts hold 30% of their daily slots as walk-in queue tickets, distributed at 7:00 AM. Arrive by 6:30 AM, take a number, expect a 3–5 hour wait. Best fallback if your online appointment is post-14-day window.
            </p>
          </div>
        </div>
      </div>
    </section>

    {/* FAQ */}
    <section className="py-14 lg:py-18 bg-white">
      <div className="max-w-4xl mx-auto px-6">
        <h2 className="text-2xl lg:text-3xl font-bold font-syne text-[#003087] mb-7">Frequently Asked Questions</h2>
        <div className="space-y-3">
          {FAQS.map((f, i) => (
            <details key={i} className="group bg-slate-50 border border-slate-200 rounded-xl p-5">
              <summary className="font-syne font-semibold text-slate-900 cursor-pointer text-[15px] flex items-start justify-between">
                <span className="pr-6">{f.q}</span>
                <span className="text-[#003087] group-open:rotate-45 transition-transform text-xl leading-none">+</span>
              </summary>
              <p className="mt-3 text-sm leading-7 text-slate-700">{f.a}</p>
            </details>
          ))}
        </div>
      </div>
    </section>
  </GermanyResourceShell>
);

export default GermanyStudentAccommodationPage;
